import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { LoginPage, RegisterPage } from "@/components/AuthPages";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import DvrWizard from "@/pages/DvrWizard";
import RiskCatalog from "@/pages/RiskCatalog";
import TechnicalModules from "@/pages/TechnicalModules";
import RiskAssessments from "@/pages/RiskAssessments";
import ImprovementPlan from "@/pages/ImprovementPlan";
import HealthTrainingPpe from "@/pages/HealthTrainingPpe";
import Documents from "@/pages/Documents";
import ReportDvr from "@/pages/ReportDvr";
import Deadlines from "@/pages/Deadlines";
import DvrPreview from "@/pages/DvrPreview";
import EmergencyPlan from "@/pages/EmergencyPlan";
import DocumentTemplates from "@/pages/DocumentTemplates";
import CsvImport from "@/pages/CsvImport";
import AdminPanel from "@/pages/AdminPanel";
import AuditLogs from "@/pages/AuditLogs";
import Analytics from "@/pages/Analytics";
import LeadCrm from "@/pages/LeadCrm";
import Landing from "@/pages/Landing";
import Exports from "@/pages/Exports";
import DvrRevisions from "@/pages/DvrRevisions";
import Newsletter from "@/pages/Newsletter";
import AiSuggestions from "@/pages/AiSuggestions";
import CompanyDocuments from "@/pages/CompanyDocuments";
import CompanyDashboard from "@/pages/CompanyDashboard";
import CustomerLeaderboard from "@/pages/CustomerLeaderboard";
import HealthAlerts from "@/pages/HealthAlerts";
import AdminBackup from "@/pages/AdminBackup";
import DvrValidation from "@/pages/DvrValidation";
import SiteInspections from "@/pages/SiteInspections";
import SafetySurvey from "@/pages/SafetySurvey";
import SafetyInbox from "@/pages/SafetyInbox";
import CrossCompanyAnalytics from "@/pages/CrossCompanyAnalytics";
import AuditsAdmin from "@/pages/AuditsAdmin";
import AuditReview from "@/pages/AuditReview";
import AuditorInvite from "@/pages/AuditorInvite";
import AuditorShell from "@/pages/auditor/AuditorShell";
import AuditorHome from "@/pages/auditor/AuditorHome";
import AuditorChecklist from "@/pages/auditor/AuditorChecklist";
import FieldShell from "@/pages/field/FieldShell";
import FieldHome from "@/pages/field/FieldHome";
import FieldSurvey from "@/pages/field/FieldSurvey";
import FieldNew from "@/pages/field/FieldNew";
import AuditorsAdmin from "@/pages/AuditorsAdmin";
import AccessLog from "@/pages/AccessLog";
import PlaceholderPage from "@/pages/PlaceholderPage";
import { Toaster } from "@/components/ui/sonner";

const placeholders = [];

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="auth-loading-screen">Caricamento DVR ProSuite...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "auditor_esterno") return <Navigate to="/auditor/" replace />; // l'auditor vede solo la sua app da campo
  if (user.role === "auditor") return <Navigate to="/campo" replace />; // l'auditor interno vede solo la sua app da campo
  return <AppLayout />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/landing" element={<Landing />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auditor/invito/:token" element={<AuditorInvite />} />
          <Route path="/auditor" element={<AuditorShell />}>
            <Route index element={<AuditorHome />} />
            <Route path="audit/:id" element={<AuditorChecklist />} />
          </Route>
          <Route path="/campo" element={<FieldShell />}>
            <Route index element={<FieldHome />} />
            <Route path="nuovo" element={<FieldNew />} />
            <Route path=":id" element={<FieldSurvey />} />
          </Route>
          <Route element={<ProtectedRoute />}>
            <Route index element={<Dashboard />} />
            <Route path="/aziende" element={<Companies />} />
            <Route path="/aziende/:id" element={<CompanyDetail />} />
            <Route path="/aziende/:id/dvr-wizard" element={<DvrWizard />} />
            <Route path="/aziende/:id/moduli-tecnici" element={<TechnicalModules />} />
            <Route path="/aziende/:id/rischi" element={<RiskAssessments />} />
            <Route path="/aziende/:id/piano-miglioramento" element={<ImprovementPlan />} />
            <Route path="/aziende/:id/formazione-dpi-sanitaria" element={<HealthTrainingPpe />} />
            <Route path="/aziende/:id/report-dvr" element={<ReportDvr />} />
            <Route path="/aziende/:id/dvr-anteprima" element={<DvrPreview />} />
            <Route path="/aziende/:id/gestione-emergenze" element={<EmergencyPlan />} />
            <Route path="/aziende/:id/esportazioni" element={<Exports />} />
            <Route path="/aziende/:id/revisioni-dvr" element={<DvrRevisions />} />
            <Route path="/aziende/:id/newsletter" element={<Newsletter />} />
            <Route path="/aziende/:id/ai-suggestions" element={<AiSuggestions />} />
            <Route path="/aziende/:id/documenti" element={<CompanyDocuments />} />
            <Route path="/aziende/:id/dashboard" element={<CompanyDashboard />} />
            <Route path="/aziende/:id/validazione-dvr" element={<DvrValidation />} />
            <Route path="/aziende/:id/sopralluoghi" element={<SiteInspections />} />
            <Route path="/aziende/:id/sopralluogo-guidato" element={<SafetySurvey />} />
            <Route path="/sopralluoghi-guidati" element={<SafetyInbox />} />
            <Route path="/auditor-interni" element={<AuditorsAdmin />} />
            <Route path="/registro-accessi" element={<AccessLog />} />
            <Route path="/audit-esterni" element={<AuditsAdmin />} />
            <Route path="/audit-esterni/:id" element={<AuditReview />} />
            <Route path="/esportazioni" element={<Exports />} />
            <Route path="/dvr-wizard" element={<DvrWizard />} />
            <Route path="/catalogo-rischi" element={<RiskCatalog />} />
            <Route path="/mansioni" element={<TechnicalModules defaultTab="mansioni" />} />
            <Route path="/ambienti" element={<TechnicalModules defaultTab="ambienti" />} />
            <Route path="/attrezzature" element={<TechnicalModules defaultTab="attrezzature" />} />
            <Route path="/sostanze" element={<TechnicalModules defaultTab="sostanze" />} />
            <Route path="/rischi" element={<RiskAssessments />} />
            <Route path="/piano-miglioramento" element={<ImprovementPlan />} />
            <Route path="/formazione" element={<HealthTrainingPpe defaultTab="formazione" />} />
            <Route path="/dpi" element={<HealthTrainingPpe defaultTab="dpi" />} />
            <Route path="/sorveglianza-sanitaria" element={<HealthTrainingPpe defaultTab="sanitaria" />} />
            <Route path="/documenti" element={<Documents />} />
            <Route path="/report-dvr" element={<ReportDvr />} />
            <Route path="/scadenze" element={<Deadlines />} />
            <Route path="/template-documenti" element={<DocumentTemplates />} />
            <Route path="/import-csv" element={<CsvImport />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/leads" element={<LeadCrm />} />
            <Route path="/audit-log" element={<AuditLogs />} />
            <Route path="/admin/audit-log" element={<AuditLogs />} />
            <Route path="/analytics" element={<CrossCompanyAnalytics />} />
            <Route path="/leaderboard" element={<CustomerLeaderboard />} />
            <Route path="/health-alerts" element={<HealthAlerts />} />
            <Route path="/admin/backup" element={<AdminBackup />} />
            <Route path="/analytics/legacy" element={<Analytics />} />
            {placeholders.map(([path, title, description]) => <Route key={path} path={path} element={<PlaceholderPage title={title} description={description} />} />)}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
